import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, Section, Button } from './index';

/**
 * Token Details section displaying BigCash token statistics and information
 */
const TokenDetails = () => {
  // Token stats from Etherscan data
  const tokenStats = [
    {
      label: 'Max Total Supply',
      value: '50,000,000 BGC',
      icon: '📊'
    },
    {
      label: 'Total Holders',
      value: '4,524',
      icon: '👥'
    },
    {
      label: 'Contract Address',
      value: '0xd043f70f...5be52d72c',
      fullValue: '0xd043f70ff0634167db1149b15e5668f5be52d72c',
      icon: '📋'
    },
    {
      label: 'Token Standard',
      value: 'ERC-20',
      icon: '⚡'
    },
    {
      label: 'Network',
      value: 'Ethereum',
      icon: '🔗'
    },
    {
      label: 'Launch Date',
      value: 'Nov 2018',
      icon: '🚀'
    }
  ];
  
  const [copiedAddress, setCopiedAddress] = useState(false);
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    });
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };
  
  return (
    <Section id="token-details" background="default" className="relative">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-coffee-200 rounded-full opacity-10"></div>
        <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-coffee-300 rounded-full opacity-15"></div>
      </div>
      
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        className="relative z-10"
      >
        {/* Section Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
            <span className="gradient-text">Token Details</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-coffee-600 to-coffee-800 mx-auto mb-8"></div>
          <p className="text-lg text-coffee-600 max-w-2xl mx-auto">
            Explore the technical details and current statistics of BigCash Coffee Token (BGC)
          </p>
        </motion.div>
        
        {/* Token Stats Grid */}
        <motion.div variants={itemVariants} className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tokenStats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                className="group"
              >
                <Card className="text-center group-hover:shadow-xl transition-all duration-300">
                  <div className="text-3xl mb-3">{stat.icon}</div>
                  <h3 className="text-lg font-semibold text-coffee-800 mb-2">
                    {stat.label}
                  </h3>
                  {stat.label === 'Contract Address' ? (
                    <div className="space-y-2">
                      <p className="text-coffee-600 font-mono text-sm">
                        {stat.value}
                      </p>
                      <button
                        onClick={() => copyToClipboard(stat.fullValue)}
                        className="text-xs text-coffee-500 hover:text-coffee-700 transition-colors"
                      >
                        {copiedAddress ? '✅ Copied!' : '📋 Copy Full Address'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-coffee-600 font-semibold">
                      {stat.value}
                    </p>
                  )}
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
        
        {/* Etherscan Integration */}
        <motion.div variants={itemVariants} className="text-center">
          <Card variant="gradient" className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-coffee-800 mb-6">
              Verify on Blockchain
            </h3>
            <p className="text-coffee-600 mb-8 leading-relaxed">
              BigCash Coffee Token is fully verified on Etherscan. View real-time transactions, 
              holder information, and contract details on the Ethereum blockchain.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                variant="primary"
                size="lg"
                href="https://etherscan.io/token/0xd043f70ff0634167db1149b15e5668f5be52d72c"
                className="w-full sm:w-auto"
              >
                View on Etherscan
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={() => copyToClipboard('0xd043f70ff0634167db1149b15e5668f5be52d72c')}
                className="w-full sm:w-auto"
              >
                {copiedAddress ? 'Address Copied!' : 'Copy Contract Address'}
              </Button>
            </div>
            
            {/* Contract Verification Badge */}
            <div className="mt-8 inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              <span className="mr-2">✅</span>
              Contract Source Code Verified
            </div>
          </Card>
        </motion.div>
        
        {/* Additional Info */}
        <motion.div variants={itemVariants} className="mt-12">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="text-center">
              <h4 className="text-xl font-bold text-coffee-800 mb-4">
                🔒 Security
              </h4>
              <p className="text-coffee-600">
                Smart contract audited and verified on Etherscan for maximum security and transparency.
              </p>
            </Card>
            
            <Card className="text-center">
              <h4 className="text-xl font-bold text-coffee-800 mb-4">
                🌐 Accessibility
              </h4>
              <p className="text-coffee-600">
                Available on major DEX platforms and compatible with all ERC-20 wallets.
              </p>
            </Card>
          </div>
        </motion.div>
      </motion.div>
    </Section>
  );
};

export default TokenDetails;